import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { C, FONTS } from '../../tokens/design';
import { useMspClients, useMspSummary } from '../../hooks/useMspData';
import { useApiClient } from '../../api/apiClient';
import { useClient } from '../../context/ClientContext';
import { relativeTime, exactTime } from '../../utils/time';
import Shell from '../../components/layout/Shell';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Tag from '../../components/ui/Tag';
import TierBadge from '../../components/badges/TierBadge';
import Skeleton from '../../components/ui/Skeleton';
import { TH, TR, TD } from '../../components/ui/Table';
import PageError from '../../components/ui/PageError';

const TIER_MAP = { Baseline: 'Baseline', BaselinePlus: 'Baseline+', Supreme: 'Supreme' };

function ScanConfirmModal({ client, onClose, api }) {
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const name = client.name ?? client.companyName ?? 'this client';
  const id = client.clientId ?? client.id;

  const handleConfirm = async () => {
    setScanning(true);
    try {
      const res = await api.post(`/clients/${id}/scans`, { profileName: 'full' });
      setResult({ ok: true, msg: 'Scan queued successfully.' });
    } catch (err) {
      setResult({ ok: false, msg: `Failed: ${err.message}` });
    } finally {
      setScanning(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }} onClick={onClose}>
      <div style={{ background: C.s1, border: `1px solid ${C.b1}`, borderRadius: 10, padding: 28, maxWidth: 420, width: '90%' }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 15, fontWeight: 700, color: C.t1, fontFamily: FONTS.sans, marginBottom: 10 }}>Queue Full Scan</div>
        <div style={{ fontSize: 12, color: C.t2, fontFamily: FONTS.mono, marginBottom: 20, lineHeight: 1.6 }}>
          Queue a full scan for <strong style={{ color: C.t1 }}>{name}</strong>? This will run all configured scan profiles.
        </div>
        {result && (
          <div style={{ marginBottom: 16, padding: '8px 12px', borderRadius: 6, fontSize: 11, fontFamily: FONTS.mono, background: result.ok ? `${C.grn}12` : C.redBg, border: `1px solid ${result.ok ? C.grn : C.red}33`, color: result.ok ? C.grn : C.red }}>
            {result.msg}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="sec" onClick={onClose}>{result ? 'Close' : 'Cancel'}</Button>
          {!result && <Button variant="pri" onClick={handleConfirm} disabled={scanning}>{scanning ? 'Queuing...' : 'Confirm Scan'}</Button>}
        </div>
      </div>
    </div>
  );
}

function BulkImportModal({ client, onClose, api }) {
  const fileRef = useRef(null);
  const [fileName, setFileName] = useState('');
  const [findings, setFindings] = useState(null);
  const [parseError, setParseError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const name = client.name ?? client.companyName ?? 'this client';
  const id = client.clientId ?? client.id;

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setParseError('');
    setFindings(null);
    setResult(null);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const parsed = JSON.parse(ev.target.result);
        const arr = Array.isArray(parsed) ? parsed : parsed.findings;
        if (!Array.isArray(arr)) {
          setParseError('JSON must be an array or contain a "findings" array.');
          return;
        }
        setFindings(arr);
      } catch {
        setParseError('Invalid JSON file.');
      }
    };
    reader.readAsText(file);
  };

  const handleUpload = async () => {
    if (!findings) return;
    setUploading(true);
    try {
      const res = await api.post(`/clients/${id}/findings/bulk`, { findings });
      setResult({ ok: true, msg: `Successfully imported ${res.importedCount ?? findings.length} findings.` });
    } catch (err) {
      setResult({ ok: false, msg: `Import failed: ${err.message}` });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }} onClick={onClose}>
      <div style={{ background: C.s1, border: `1px solid ${C.b1}`, borderRadius: 10, padding: 28, maxWidth: 520, width: '90%' }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 15, fontWeight: 700, color: C.t1, fontFamily: FONTS.sans, marginBottom: 6 }}>Import Findings</div>
        <div style={{ fontSize: 11, color: C.t2, fontFamily: FONTS.mono, marginBottom: 16 }}>Upload a JSON file for <strong style={{ color: C.t1 }}>{name}</strong></div>

        <div
          onClick={() => fileRef.current?.click()}
          style={{ border: `2px dashed ${C.b1}`, borderRadius: 8, padding: '24px 16px', textAlign: 'center', cursor: 'pointer', marginBottom: 12, background: C.s2 }}
        >
          <input ref={fileRef} type="file" accept=".json" onChange={handleFile} style={{ display: 'none' }} />
          <div style={{ fontSize: 12, color: C.t2, fontFamily: FONTS.mono }}>
            {fileName ? fileName : 'Click to select .json file'}
          </div>
          {findings && <div style={{ fontSize: 11, color: C.grn, fontFamily: FONTS.mono, marginTop: 6 }}>{findings.length} findings parsed</div>}
        </div>

        {parseError && (
          <div style={{ marginBottom: 12, fontSize: 11, color: C.red, fontFamily: FONTS.mono }}>{parseError}</div>
        )}

        <div style={{ fontSize: 9, color: C.t3, fontFamily: FONTS.mono, marginBottom: 16, lineHeight: 1.6 }}>
          Expected format: [{'{'} "title": "...", "severity": "High", "affectedAsset": "...", "sourceModule": "..." {'}'}]
        </div>

        {result && (
          <div style={{ marginBottom: 16, padding: '8px 12px', borderRadius: 6, fontSize: 11, fontFamily: FONTS.mono, background: result.ok ? `${C.grn}12` : C.redBg, border: `1px solid ${result.ok ? C.grn : C.red}33`, color: result.ok ? C.grn : C.red }}>
            {result.msg}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="sec" onClick={onClose}>{result?.ok ? 'Close' : 'Cancel'}</Button>
          {!result?.ok && <Button variant="pri" onClick={handleUpload} disabled={!findings || uploading}>{uploading ? 'Uploading...' : 'Import Findings'}</Button>}
        </div>
      </div>
    </div>
  );
}

function AddClientModal({ onClose, api, onSuccess }) {
  const [name, setName] = useState('');
  const [tier, setTier] = useState(0);
  const [licenseKey, setLicenseKey] = useState('');
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const handleCreate = async () => {
    if (!name) return;
    setSaving(true);
    try {
      await api.post('/clients', { name, tier: Number(tier), licenseKey: licenseKey || undefined });
      setResult({ ok: true, msg: `Client "${name}" created successfully` });
      onSuccess();
    } catch (err) {
      setResult({ ok: false, msg: err.message || 'Failed to create client' });
    } finally {
      setSaving(false);
    }
  };

  const iStyle = {
    width: '100%', background: C.s2, border: `1px solid ${C.b1}`, borderRadius: 6,
    padding: '8px 10px', color: C.t1, fontSize: 12, fontFamily: FONTS.mono, outline: 'none',
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }} onClick={onClose}>
      <div style={{ background: C.s1, border: `1px solid ${C.b1}`, borderRadius: 10, padding: 28, maxWidth: 440, width: '90%' }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 15, fontWeight: 700, color: C.t1, fontFamily: FONTS.sans, marginBottom: 6 }}>Add Client</div>
        <div style={{ fontSize: 11, color: C.t2, fontFamily: FONTS.mono, marginBottom: 18 }}>Create a new client organization.</div>

        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>Client Name</div>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="Acme Corp" style={iStyle} />
        </div>
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>Tier</div>
          <select value={tier} onChange={e => setTier(e.target.value)} style={{ ...iStyle, cursor: 'pointer' }}>
            <option value="Baseline">Baseline</option>
            <option value="BaselinePlus">Baseline+</option>
            <option value="Supreme">Supreme</option>
          </select>
        </div>
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>License Key (optional)</div>
          <input value={licenseKey} onChange={e => setLicenseKey(e.target.value)} placeholder="XXXX-XXXX-XXXX" style={iStyle} />
        </div>

        {result && (
          <div style={{
            marginBottom: 14, padding: '8px 12px', borderRadius: 6, fontSize: 11, fontFamily: FONTS.mono,
            background: result.ok ? `${C.grn}12` : C.redBg,
            border: `1px solid ${result.ok ? C.grn : C.red}33`,
            color: result.ok ? C.grn : C.red,
          }}>{result.msg}</div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="sec" onClick={onClose}>{result?.ok ? 'Close' : 'Cancel'}</Button>
          {!result?.ok && (
            <Button variant="pri" onClick={handleCreate} disabled={saving || !name}>
              {saving ? 'Creating...' : 'Create Client'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

const FILTERS = ['All', 'At Risk', 'No Scans', 'Incomplete Setup'];

export default function MspClients() {
  const navigate = useNavigate();
  const api = useApiClient();
  const { setClientId, setClientName } = useClient();
  const { data: clients, loading, error, refetch } = useMspClients();
  const { data: summary } = useMspSummary();
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('All');
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [scanClient, setScanClient] = useState(null);
  const [importClient, setImportClient] = useState(null);
  const [addClientOpen, setAddClientOpen] = useState(false);

  const handleClientClick = (client) => {
    const id = client.clientId ?? client.id;
    const name = client.name ?? client.companyName ?? '';
    setClientId(id);
    setClientName(name);
    navigate('/client/dashboard');
  };

  const handleDeleteClick = (client) => {
    setConfirmDelete(client);
  };

  const [deleteError, setDeleteError] = useState(null);

  const handleDeleteConfirm = async () => {
    if (!confirmDelete) return;
    const id = confirmDelete.clientId ?? confirmDelete.id;
    setDeletingId(id);
    setDeleteError(null);
    try {
      await api.delete(`/clients/${id}`);
      setConfirmDelete(null);
      if (refetch) refetch();
    } catch (err) {
      setDeleteError(err.message);
    } finally {
      setDeletingId(null);
    }
  };
  const items = clients.filter(c => {
    const matchQ = (c.name || '').toLowerCase().includes(q.toLowerCase()) ||
      (c.industry || '').toLowerCase().includes(q.toLowerCase());
    if (!matchQ) return false;
    if (filter === 'At Risk') {
      const score = c.score ?? null;
      return typeof score === 'number' && score < 60;
    }
    if (filter === 'No Scans') return !c.lastScanAt && !c.lastScan && !c.last;
    if (filter === 'Incomplete Setup') return c.onboardingComplete === false || c.status === 'onboarding';
    return true;
  });

  return (
    <Shell title="Client Management" sub={`${clients.length} clients`}>
      {error && <PageError error={error} />}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center' }}>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search clients..." style={{ flex: 1, background: C.s2, border: `1px solid ${C.b1}`, borderRadius: 6, padding: '7px 10px', color: C.t1, fontSize: 12, fontFamily: FONTS.mono, outline: 'none' }} />
        <Button variant="pri" onClick={() => navigate('/msp/onboard')}>+ Add Client</Button>
      </div>

      {/* Filter buttons */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        {FILTERS.map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{
            padding: '5px 12px', borderRadius: 6, fontSize: 10, fontWeight: 600,
            fontFamily: FONTS.mono, cursor: 'pointer', transition: 'all 0.12s',
            background: filter === f ? C.s2 : 'transparent',
            border: `1px solid ${filter === f ? C.b1 : 'transparent'}`,
            color: filter === f ? C.t1 : C.t2,
          }}>{f}</button>
        ))}
      </div>

      {loading && (
        <Card>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <TH cols={['Client', 'Tier', 'Score', 'Findings', 'Users', 'Last Scan', 'Status', '']} />
            <tbody>
              {[0, 1, 2].map(i => (
                <tr key={i} style={{ borderBottom: `1px solid ${C.b2}` }}>
                  <TD><div><Skeleton width={120} height={12} style={{ marginBottom: 4 }} /><Skeleton width={80} height={10} /></div></TD>
                  <TD><Skeleton width={60} height={18} /></TD>
                  <TD><Skeleton width={35} height={16} /></TD>
                  <TD><Skeleton width={50} height={18} /></TD>
                  <TD><Skeleton width={40} height={12} /></TD>
                  <TD><Skeleton width={70} height={12} /></TD>
                  <TD><Skeleton width={55} height={18} /></TD>
                  <TD><Skeleton width={80} height={24} /></TD>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {error && (
        <Card style={{ padding: 20 }}>
          <div style={{ fontSize: 11, color: C.red, fontFamily: FONTS.mono }}>{error}</div>
        </Card>
      )}

      {!loading && !error && clients.length === 0 && (
        <Card style={{ padding: 32, textAlign: 'center' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.t1, fontFamily: FONTS.sans, marginBottom: 8 }}>No clients onboarded yet</div>
          <div style={{ fontSize: 11, color: C.t2, fontFamily: FONTS.mono, marginBottom: 16 }}>Use Client Onboarding to add your first client.</div>
          <Button variant="pri" onClick={() => navigate('/msp/onboard')}>+ Add Client</Button>
        </Card>
      )}

      {!loading && !error && clients.length > 0 && (
        <Card>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <TH cols={['Client', 'Tier', 'Score', 'Findings', 'Users', 'Last Scan', 'Status', '']} />
            <tbody>{items.map(c => {
              const id = c.clientId ?? c.id ?? '';
              const name = c.name ?? c.companyName ?? '';
              const tier = TIER_MAP[c.tier] || c.tier || 'Baseline';
              const industry = c.industry ?? '';
              const users = c.userCount ?? c.employeeCount ?? c.users ?? 0;
              const sc2 = Array.isArray(summary?.clients) ? summary.clients.find(s => (s.clientId ?? s.id)?.toLowerCase() === id?.toLowerCase()) : null;
              const score = sc2?.score ?? c.score ?? null;
              const delta = c.delta ?? null;
              const crit = sc2?.crit ?? c.crit ?? 0;
              const high = sc2?.high ?? c.high ?? 0;
              const last = sc2?.lastScanAt ?? c.lastScanAt ?? c.lastScan ?? c.last ?? c.createdAt ?? null;
              const status = c.status ?? 'Active';
              const sc = typeof score === 'number' ? (score >= 80 ? C.grn : score >= 60 ? C.amb : C.red) : C.t3;
              return (
                <TR key={id} onClick={() => handleClientClick(c)}>
                  <TD><div style={{ fontSize: 12, fontWeight: 600, color: C.t1 }}>{name}</div>{industry && <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginTop: 1 }}>{industry}</div>}</TD>
                  <TD><TierBadge value={tier} /></TD>
                  <TD><span style={{ fontSize: 15, fontWeight: 800, color: sc, fontFamily: FONTS.mono }}>{typeof score === 'number' ? score : '\u2014'}</span>{typeof score === 'number' && typeof delta === 'number' && <span style={{ fontSize: 9, color: delta >= 0 ? C.grn : C.red, fontFamily: FONTS.mono, marginLeft: 3 }}>{delta >= 0 ? '+' : ''}{delta}</span>}</TD>
                  <TD><div style={{ display: 'flex', gap: 4 }}>{crit > 0 && <Tag label={`${crit}C`} color={C.red} />}{high > 0 && <Tag label={`${high}H`} color={C.org} />}{crit === 0 && high === 0 && <span style={{ fontSize: 10, color: C.t3, fontFamily: FONTS.mono }}>{'\u2014'}</span>}</div></TD>
                  <TD style={{ fontSize: 11, color: C.t2, fontFamily: FONTS.mono }}>{(users ?? 0).toLocaleString()}</TD>
                  <TD style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono }} title={last ? exactTime(last) : ''}>{last ? relativeTime(last) : '\u2014'}</TD>
                  <TD>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      <div style={{ width: 6, height: 6, borderRadius: '50%', background: c.onboardingComplete === false ? C.amb : C.grn, flexShrink: 0 }} title={c.onboardingComplete === false ? 'Onboarding incomplete' : 'Setup complete'} />
                      <Tag label={status} color={status === 'warning' ? C.red : C.grn} />
                    </div>
                  </TD>
                  <TD>
                    <div style={{ display: 'flex', gap: 5 }}>
                      <Button variant="ghost" onClick={(e) => { e.stopPropagation(); handleClientClick(c); }}>View {'\u2192'}</Button>
                      <Button variant="sec" onClick={(e) => { e.stopPropagation(); setScanClient(c); }}>Scan</Button>
                      <Button variant="sec" onClick={(e) => { e.stopPropagation(); setImportClient(c); }} style={{ fontSize: 10 }}>Import</Button>
                      <Button variant="ghost" onClick={(e) => { e.stopPropagation(); handleDeleteClick(c); }} style={{ color: C.red, fontSize: 11, padding: '4px 8px' }}>{'\u2715'}</Button>
                    </div>
                  </TD>
                </TR>
              );
            })}</tbody>
          </table>
        </Card>
      )}

      {confirmDelete && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }} onClick={() => setConfirmDelete(null)}>
          <div style={{ background: C.s1, border: `1px solid ${C.b1}`, borderRadius: 10, padding: 28, maxWidth: 420, width: '90%' }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.t1, fontFamily: FONTS.sans, marginBottom: 10 }}>Deactivate Client</div>
            <div style={{ fontSize: 12, color: C.t2, fontFamily: FONTS.mono, marginBottom: 14, lineHeight: 1.6 }}>
              Are you sure you want to remove <strong style={{ color: C.t1 }}>{confirmDelete.name ?? confirmDelete.companyName}</strong>?
            </div>
            <div style={{ fontSize: 11, color: C.t2, fontFamily: FONTS.mono, marginBottom: 20, lineHeight: 1.8 }}>
              This will deactivate the client and:{'\n'}
              <span style={{ color: C.t1 }}>{'\u2022'}</span> Stop all scheduled scans{'\n'}
              <span style={{ color: C.t1 }}>{'\u2022'}</span> Hide the client from your dashboard{'\n'}
              <span style={{ color: C.t1 }}>{'\u2022'}</span> Preserve historical data for compliance purposes{'\n\n'}
              <span style={{ fontSize: 9, color: C.t3, fontStyle: 'italic' }}>To permanently delete all client data, contact Southern Sentinel Security.</span>
            </div>
            {deleteError && (
              <div style={{ marginBottom: 14, padding: '8px 12px', borderRadius: 6, fontSize: 11, fontFamily: FONTS.mono, background: C.redBg, border: `1px solid ${C.red}33`, color: C.red }}>{deleteError}</div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button variant="sec" onClick={() => setConfirmDelete(null)}>Cancel</Button>
              <Button variant="pri" onClick={handleDeleteConfirm} disabled={!!deletingId} style={{ background: C.red }}>{deletingId ? 'Deactivating...' : 'Deactivate Client'}</Button>
            </div>
          </div>
        </div>
      )}

      {scanClient && <ScanConfirmModal client={scanClient} onClose={() => setScanClient(null)} api={api} />}
      {importClient && <BulkImportModal client={importClient} onClose={() => setImportClient(null)} api={api} />}
      {addClientOpen && <AddClientModal onClose={() => setAddClientOpen(false)} api={api} onSuccess={() => refetch()} />}
    </Shell>
  );
}
